import React, { HTMLAttributes, KeyboardEvent, useCallback } from 'react';
import BiSpan from '@wix/da-bi/pkg/Components/BiSpan';

interface Props extends HTMLAttributes<HTMLButtonElement> {}

/**
 * A button made from a 'span'. For use in situations we need to nest buttons. Handles all the necessary events
 * to make it just like a real button
 */
const FakeButton: React.FC<Props> = ({
  onClick = () => null,
  onKeyDown = () => null,
  ...props
}) => {
  /**
   * Our fake button is mostly used in contexts where we need to nest a button inside of another button.
   *
   * Due to event propagation, keydown events trigger the fake button's click and the real button's click.
   * We don't want that.
   */
  const handleKeyDownForFakeButton = useCallback(
    (e: KeyboardEvent<any>) => {
      switch (e.key) {
        case ' ':
        case 'Space':
        case 'Enter':
          onClick(e as any);
          e.stopPropagation();
          break;
        default:
          break;
      }
      onKeyDown(e);
    },
    [onKeyDown, onClick]
  );

  return (
    <BiSpan
      tabIndex={0}
      role="button"
      onClick={onClick}
      onKeyDown={handleKeyDownForFakeButton}
      {...props}
    />
  );
};
FakeButton.displayName = 'FakeButton';

export default FakeButton;
